import React, { useState } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Chip from "@material-ui/core/Chip";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";

import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AssessmentIcon from "@material-ui/icons/Assessment";
import SendIcon from "@material-ui/icons/Send";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import MetaCampaignModal from "../../components/MetaCampaignModal";

import { useMetaCampaigns } from "./hooks/useMetaCampaigns";

const useStyles = makeStyles((theme) => ({
  mainPaper: { flex: 1, padding: theme.spacing(1), overflowY: "scroll", ...theme.scrollbarStyles },
  searchRow: { display: "flex", gap: theme.spacing(2), alignItems: "center", padding: theme.spacing(1, 2) },
  searchField: { flex: 1, minWidth: 200 },
  chip: { fontSize: "0.72rem", fontWeight: 600 },
  helpText: { display: "block", padding: theme.spacing(0, 2, 1), color: theme.palette.text.secondary },
}));

const STATUS_STYLE = {
  INATIVA: { label: "Inactiva", color: "#616161", background: "#f5f5f5" },
  PROGRAMADA: { label: "Programada", color: "#1565c0", background: "#e3f2fd" },
  EM_ANDAMENTO: { label: "En curso", color: "#e65100", background: "#fff3e0" },
  CANCELADA: { label: "Cancelada", color: "#b71c1c", background: "#ffebee" },
  FINALIZADA: { label: "Finalizada", color: "#1b5e20", background: "#e8f5e9" },
};

const StatusChip = ({ status }) => {
  const classes = useStyles();
  const s = STATUS_STYLE[status] || { label: status || "—", color: "#616161", background: "#f5f5f5" };
  return <Chip size="small" label={s.label} className={classes.chip} style={{ color: s.color, backgroundColor: s.background }} />;
};

export default function MetaCampaigns() {
  const classes = useStyles();
  const history = useHistory();
  const { campaigns, templateNameById, loading, searchParam, fetchCampaigns, handleSearch, handleDelete } =
    useMetaCampaigns();

  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const openCreate = () => {
    setEditTarget(null);
    setModalOpen(true);
  };
  const openEdit = (campaign) => {
    setEditTarget(campaign);
    setModalOpen(true);
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>
          <SendIcon style={{ marginRight: 8, verticalAlign: "middle" }} />
          Campañas Meta
        </Title>
        <MainHeaderButtonsWrapper>
          <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={openCreate}>
            Nueva Campaña
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <div className={classes.searchRow}>
          <TextField
            className={classes.searchField}
            placeholder="Buscar campaña..."
            value={searchParam}
            onChange={(e) => handleSearch(e.target.value)}
            InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }}
            variant="outlined"
            size="small"
          />
        </div>

        <Typography variant="caption" className={classes.helpText}>
          Envío masivo por <strong>plantilla aprobada</strong> a través de una conexión de WhatsApp
          Cloud API (Meta). Las variables de la plantilla se mapean a columnas de la lista de contactos.
        </Typography>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nombre</TableCell>
              <TableCell align="center">Estado</TableCell>
              <TableCell>Plantilla</TableCell>
              <TableCell>Conexión</TableCell>
              <TableCell>Lista de Contactos</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRowSkeleton columns={6} />
            ) : campaigns.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="textSecondary" style={{ padding: 16 }}>
                    No hay campañas Meta. Crea una nueva.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              campaigns.map((c) => (
                <TableRow key={c.id} hover>
                  <TableCell><strong>{c.name}</strong></TableCell>
                  <TableCell align="center"><StatusChip status={c.status} /></TableCell>
                  <TableCell>{templateNameById[c.templateId] || `#${c.templateId}`}</TableCell>
                  <TableCell>{c.whatsapp?.name || `#${c.whatsappId ?? "—"}`}</TableCell>
                  <TableCell>{c.contactList?.name || `#${c.contactListId ?? "—"}`}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Ver reporte / métricas">
                      <IconButton size="small" onClick={() => history.push(`/campaign/${c.id}/report`)}>
                        <AssessmentIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => openEdit(c)}><EditIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                      <IconButton size="small" onClick={() => setDeleteTarget(c)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      <MetaCampaignModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        campaign={editTarget}
        onSaved={() => fetchCampaigns(searchParam)}
      />

      <ConfirmationModal
        title={`¿Eliminar la campaña "${deleteTarget?.name}"?`}
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget) handleDelete(deleteTarget.id);
          setDeleteTarget(null);
        }}
      >
        Esta acción no se puede deshacer.
      </ConfirmationModal>
    </MainContainer>
  );
}
